import { z } from 'zod';
import { TARGET_MARKETS, QUOTE_SIDES, SOURCE_KINDS } from './types.js';
export const marketSchema = z.enum(TARGET_MARKETS);
export const quoteSideSchema = z.enum(QUOTE_SIDES);
export const sourceKindSchema = z.enum(SOURCE_KINDS);
export const matchMetaSchema = z.object({
    scheduleId: z.string().min(1),
    leagueCode: z.string().min(1),
    leagueName: z.string(),
    homeTeamId: z.string(),
    awayTeamId: z.string(),
    homeTeamName: z.string(),
    awayTeamName: z.string(),
    kickoffTime: z.string(),
    status: z.enum(['scheduled', 'live', 'finished', 'postponed', 'cancelled', 'unknown']),
    homeGoals: z.number().int().nonnegative().optional(),
    awayGoals: z.number().int().nonnegative().optional(),
    halfHomeGoals: z.number().int().nonnegative().optional(),
    halfAwayGoals: z.number().int().nonnegative().optional(),
});
export const oddsQuoteSchema = z.object({
    providerKey: z.string().min(1),
    providerName: z.string().min(1),
    market: marketSchema,
    side: quoteSideSchema,
    lineText: z.string().optional(),
    lineValue: z.number().optional(),
    initialLineText: z.string().optional(),
    initialLineValue: z.number().optional(),
    liveLineText: z.string().optional(),
    liveLineValue: z.number().optional(),
    initialOdds: z.number().positive().optional(),
    liveOdds: z.number().positive().optional(),
    initialProbability: z.number().optional(),
    liveProbability: z.number().optional(),
    initialReturnRate: z.number().optional(),
    liveReturnRate: z.number().optional(),
    kelly: z.number().optional(),
    sourceUpdatedAt: z.string().optional(),
    rawFields: z.record(z.union([z.string(), z.number(), z.null()])),
});
export const ingestionPayloadSchema = z.object({
    sourceKind: sourceKindSchema,
    scheduleId: z.string().optional(),
    teamId: z.string().optional(),
    sourceUrl: z.string().url(),
    fetchedAt: z.string().datetime().optional(),
    contentHash: z.string().min(16),
    rawText: z.string().max(2_000_000),
    meta: matchMetaSchema.optional(),
    quotes: z.array(oddsQuoteSchema).default([]),
    movements: z.array(z.object({
        providerKey: z.string(),
        providerName: z.string(),
        market: marketSchema,
        side: quoteSideSchema,
        sequence: z.number().int().nonnegative(),
        lineText: z.string().optional(),
        lineValue: z.number().optional(),
        odds: z.number().positive(),
        observedAt: z.string().optional(),
        rawFields: z.record(z.union([z.string(), z.number(), z.null()])),
    })).default([]),
    analysisData: z.object({ scoreAll: z.array(z.object({ rank: z.number().nullable(), teamId: z.string(), nameTw: z.string(), nameCn: z.string(), played: z.number().nullable(), wins: z.number().nullable(), draws: z.number().nullable(), losses: z.number().nullable(), goalsFor: z.number().nullable(), goalsAgainst: z.number().nullable(), goalDifference: z.number().nullable(), winRate: z.number().nullable(), drawRate: z.number().nullable(), lossRate: z.number().nullable(), goalsForAvg: z.number().nullable(), goalsAgainstAvg: z.number().nullable(), points: z.number().nullable(), note: z.string().nullable() })), scoreHome: z.array(z.object({ rank: z.number().nullable(), teamId: z.string(), nameTw: z.string(), nameCn: z.string(), played: z.number().nullable(), wins: z.number().nullable(), draws: z.number().nullable(), losses: z.number().nullable(), goalsFor: z.number().nullable(), goalsAgainst: z.number().nullable(), goalDifference: z.number().nullable(), winRate: z.number().nullable(), drawRate: z.number().nullable(), lossRate: z.number().nullable(), goalsForAvg: z.number().nullable(), goalsAgainstAvg: z.number().nullable(), points: z.number().nullable(), note: z.string().nullable() })), scoreGuest: z.array(z.object({ rank: z.number().nullable(), teamId: z.string(), nameTw: z.string(), nameCn: z.string(), played: z.number().nullable(), wins: z.number().nullable(), draws: z.number().nullable(), losses: z.number().nullable(), goalsFor: z.number().nullable(), goalsAgainst: z.number().nullable(), goalDifference: z.number().nullable(), winRate: z.number().nullable(), drawRate: z.number().nullable(), lossRate: z.number().nullable(), goalsForAvg: z.number().nullable(), goalsAgainstAvg: z.number().nullable(), points: z.number().nullable(), note: z.string().nullable() })), halfAll: z.array(z.object({ rank: z.number().nullable(), teamId: z.string(), nameTw: z.string(), nameCn: z.string(), played: z.number().nullable(), wins: z.number().nullable(), draws: z.number().nullable(), losses: z.number().nullable(), goalsFor: z.number().nullable(), goalsAgainst: z.number().nullable(), goalDifference: z.number().nullable(), winRate: z.number().nullable(), drawRate: z.number().nullable(), lossRate: z.number().nullable(), goalsForAvg: z.number().nullable(), goalsAgainstAvg: z.number().nullable(), points: z.number().nullable(), note: z.string().nullable() })), halfHome: z.array(z.object({ rank: z.number().nullable(), teamId: z.string(), nameTw: z.string(), nameCn: z.string(), played: z.number().nullable(), wins: z.number().nullable(), draws: z.number().nullable(), losses: z.number().nullable(), goalsFor: z.number().nullable(), goalsAgainst: z.number().nullable(), goalDifference: z.number().nullable(), winRate: z.number().nullable(), drawRate: z.number().nullable(), lossRate: z.number().nullable(), goalsForAvg: z.number().nullable(), goalsAgainstAvg: z.number().nullable(), points: z.number().nullable(), note: z.string().nullable() })), halfGuest: z.array(z.object({ rank: z.number().nullable(), teamId: z.string(), nameTw: z.string(), nameCn: z.string(), played: z.number().nullable(), wins: z.number().nullable(), draws: z.number().nullable(), losses: z.number().nullable(), goalsFor: z.number().nullable(), goalsAgainst: z.number().nullable(), goalDifference: z.number().nullable(), winRate: z.number().nullable(), drawRate: z.number().nullable(), lossRate: z.number().nullable(), goalsForAvg: z.number().nullable(), goalsAgainstAvg: z.number().nullable(), points: z.number().nullable(), note: z.string().nullable() })) }).optional(),
    liveMatches: z.array(z.object({
        scheduleId: z.string(),
        leagueCode: z.string(),
        leagueName: z.string(),
        kickoffTime: z.string(),
        status: z.enum(['scheduled', 'live', 'finished', 'postponed', 'cancelled', 'unknown']),
        phaseText: z.string(),
        minute: z.number().int().nullable(),
        homeTeamName: z.string(),
        awayTeamName: z.string(),
        homeGoals: z.number().nullable(),
        awayGoals: z.number().nullable(),
        halfHomeGoals: z.number().nullable(),
        halfAwayGoals: z.number().nullable(),
        homeRed: z.number().nullable(),
        awayRed: z.number().nullable(),
        homeYellow: z.number().nullable(),
        awayYellow: z.number().nullable(),
        homeCorners: z.number().nullable(),
        awayCorners: z.number().nullable(),
        firstAsianLine: z.string().nullable(),
        firstTotalsLine: z.string().nullable(),
        raw: z.array(z.string()),
    })).default([]),
    recentMatches: z.array(z.object({
        scheduleId: z.string(),
        leagueCode: z.string(),
        leagueName: z.string(),
        kickoffTime: z.string(),
        homeTeamId: z.string(),
        awayTeamId: z.string(),
        homeTeamName: z.string(),
        awayTeamName: z.string(),
        homeGoals: z.number().nullable(),
        awayGoals: z.number().nullable(),
        stats: z.record(z.union([z.string(), z.number(), z.null()])),
        raw: z.array(z.unknown()),
    })).default([]),
});
export const listQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    league: z.string().optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    status: z.enum(['all', 'scheduled', 'live', 'finished']).default('all'),
    from: z.string().optional(),
    to: z.string().optional(),
    team: z.string().optional(),
    q: z.string().optional(),
});
export const movementQuerySchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    company: z.string().optional(),
    market: marketSchema.optional(),
});
//# sourceMappingURL=schemas.js.map